<template>
  <base-dialog @cancel="cancelDialog" :width="1000">
    <div slot="header">{{ $t('common.create') }} {{ $t('dictionary.clouduser') }}</div>
    <div slot="body">
      <a-form-model
        ref="form"
        v-bind="formItemLayout"
        :model="form"
        :rules="rules">
        <a-form-model-item :label="$t('cloudenv.clouduser_list_t4')">
          <span>{{ params.user.name }}</span>
        </a-form-model-item>
        <a-form-model-item :label="$t('dictionary.domain')" prop="domain_id">
          <base-select
            v-model="form.domain_id"
            filterable
            resource="domains"
            isDefaultSelect
            :params="{scope: $store.getters.scope, limit: 20}" />
        </a-form-model-item>
        <a-form-model-item :label="$t('dictionary.cloudprovider')" prop="rules" :extra="$t('cloudenv.clouduser_text2')">
          <a-form-model-item v-for="(item, index) in form.rules" :key="item.key" :rules="rules.manager_id" :prop="`rules.${index}.manager_id`">
            <a-row class="d-flex">
              <span class="mr-2">{{ index + 1 }}</span>
              <a-col :span="6">
                <base-select
                  v-model="form.rules[index].provider"
                  :options="providerOptions"
                  :select-props="{placeholder:$t('common_283')}"
                  isDefaultSelect
                  @change="(val) => valueChange('provider', index, val)" />
              </a-col>
              <a-col :span="6" class="ml-2">
                <base-select
                  v-model="form.rules[index].manager_id"
                  resource="cloudproviders"
                  filterable
                  :params="cloudproviderParams[item.key]"
                  :disabledItems="disabledManager[item.key]"
                  :select-props="{placeholder: $t('dictionary.cloudprovider'), dropdownMatchSelectWidth: false}"
                  @change="(val) => valueChange('manager_id', index, val)" />
              </a-col>
              <a-col :span="10" class="ml-2">
                <base-select
                  v-model="form.rules[index].cloudgroup_ids"
                  resource="cloudgroups"
                  filterable
                  :params="cloudgroupParams[item.key]"
                  dropdownItemWordWrap
                  :select-props="{mode: 'multiple', placeholder: $t('dictionary.cloudusergroup')}"
                  @change="(val) => valueChange('cloudgroup_ids', index, val)" />
              </a-col>
              <div v-if="form.rules.length > 1"><a-button style="flex: 0 0 24px;margin-left:10px" shape="circle" icon="minus" size="small" @click="deleteRule(index)" /></div>
            </a-row>
          </a-form-model-item>
          <div class="d-flex align-items-center mt-2">
            <a-button type="primary" shape="circle" icon="plus" size="small" @click="addRule" />
            <a-button type="link" @click="addRule">{{$t('iam.add_cloud_user')}}</a-button>
          </div>
        </a-form-model-item>
        <a-form-model-item :label="$t('system.text_126')" :extra="nameExtra" prop="generate_name">
          <a-input v-model="form.generate_name" :placeholder="namePlaceholder" />
          <template #extra>
            <name-repeated
              res="cloudusers"
              version="v1"
              :name="form.generate_name" />
          </template>
        </a-form-model-item>
        <a-form-model-item :label="$t('system.text_146')" class="mb-0" :extra="isEnableMail ? '' : $t('system.is_config_mail_tips')" prop="email">
          <a-input v-model="form.email" :disabled="!isEnableMail" :placeholder="$t('system.email_placeholder')" />
        </a-form-model-item>
        <a-form-model-item :wrapperCol="{ offset: 3 }">
          <a-checkbox v-model="form.notify" :disabled="!isCanSendNotify">{{ $t('system.email_checkbox_text') }}</a-checkbox>
        </a-form-model-item>
      </a-form-model>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import get from 'lodash/get'
import * as R from 'ramda'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import NameRepeated from '@/sections/NameRepeated'
import { HYPERVISORS_MAP } from '@/constants'
import { uuid } from '@/utils/utils'
import { validateModelForm } from '@/utils/validate'

export default {
  name: 'ClouduserCreateForUserDialog',
  components: {
    NameRepeated,
  },
  mixins: [DialogMixin, WindowsMixin],
  data () {
    const providerOptions = Object.entries(HYPERVISORS_MAP).filter(item => {
      return (this.$store.getters.capability.cloud_id_brands || []).includes(item[1].provider) && this.$store.getters.capability.brands.includes(item[1].provider)
    }).map(item => {
      return {
        id: item[1].key,
        name: item[1].label,
      }
    })
    const firstProvider = get(providerOptions, '[0][1].provider')
    return {
      loading: false,
      form: {
        domain_id: undefined,
        rules: [{ key: uuid(), provider: firstProvider, manager_id: undefined, cloudgroup_ids: [] }],
        generate_name: this.params.user.name,
        email: '',
        notify: false,
      },
      rules: {
        domain_id: { required: true, message: this.$t('common.tips.select', [this.$t('dictionary.domain')]) },
        manager_id: { required: true, message: this.$t('common.tips.select', [this.$t('dictionary.cloudprovider')]) },
        rules: { required: true },
        generate_name: { required: true, message: this.$t('common.tips.input', [this.$t('system.text_126')]) },
      },
      providerOptions,
      formItemLayout: {
        wrapperCol: {
          span: 20,
        },
        labelCol: {
          span: 4,
        },
      },
      isCanSendNotify: false,
      isEnableMail: false,
    }
  },
  computed: {
    cloudproviderParams () {
      const ret = {}
      this.form.rules.forEach(item => {
        const param = {
          scope: this.$store.getters.scope,
          provider: item.provider,
        }
        ret[item.key] = param
      })
      return ret
    },
    cloudgroupParams () {
      const ret = {}
      this.form.rules.forEach(item => {
        const param = {
          scope: this.$store.getters.scope,
          provider: item.provider,
          manager_id: item.manager_id,
        }
        ret[item.key] = param
      })
      return ret
    },
    disabledManager () {
      const ret = {}
      const selectedManager = this.form.rules.map(item => item.manager_id)
      this.form.rules.forEach(item => {
        ret[item.key] = selectedManager.filter(m => m !== item.manager_id)
      })
      return ret
    },
    namePlaceholder () {
      return this.isGoogle ? this.$t('system.text_496') : this.params.user.name
    },
    nameExtra () {
      return this.isGoogle ? null : this.$t('cloudenv.clouduser_text1')
    },
  },
  destroyed () {
    this.um = null
  },
  created () {
    this.um = new this.$Manager('users', 'v1')
    // this.fetchProjects()
    this.fetchReceivers()
  },
  methods: {
    addRule () {
      this.form.rules.push({ key: uuid(), provider: this.form.rules[this.form.rules.length - 1].provider, manager_id: undefined, cloudgroup_ids: [] })
    },
    deleteRule (i) {
      this.form.rules = this.form.rules.filter((item, index) => index !== i)
    },
    valueChange (key, index, val) {
      if (key === 'provider') {
        this.form.rules[index].cloudgroup_ids = []
      }
      if (key === 'manager_id') {
        this.$refs.form.validateField('rules.' + index + '.manager_id')
      }
    },
    emailValidator (rule, value, callback) {
      if (R.isEmpty(value) || R.isNil(value)) {
        return callback()
      }
      return this.$validate('email')(rule, value, callback)
    },
    // async fetchProjects () {
    //   try {
    //     const response = await this.um.getSpecific({
    //       id: this.params.user.id,
    //       spec: 'projects',
    //       params: {
    //         scope: this.$store.getters.scope,
    //       },
    //     })
    //     this.projects = response.data.data || []
    //     if (this.projects.length) {
    //       this.form.project_id = this.projects[0].id
    //     }
    //   } catch (error) {
    //     throw error
    //   }
    // },
    async handleConfirm () {
      this.loading = true
      try {
        await validateModelForm(this.$refs.form)
        const list = this.form.rules.map((item, index) => {
          return this.params.onManager('create', {
            managerArgs: {
              data: {
                index: index + 1,
                domain_id: this.form.domain_id,
                provider: item.provider,
                manager_id: item.manager_id,
                cloudgroup_ids: item.cloudgroup_ids,
                generate_name: this.form.generate_name,
                email: this.form.email,
                notify: this.form.notify,
                owner_id: this.params.user.id,
              },
              params: { $t: uuid() + index },
            },
          })
        })
        Promise.all(list).then(res => {
          this.cancelDialog()
        }).catch(err => {
          throw err
        })
      } finally {
        this.loading = false
      }
    },
    formatterLabel (row) {
      return row.description ? `${row.name} / ${row.description}` : row.name
    },
    async fetchReceivers () {
      this.isEnableMail = false
      try {
        const params = { scope: this.$store.getters.scope, id: this.params.user.id }
        const response = await new this.$Manager('receivers', 'v1').list({ params })
        const receivers = response.data.data || []
        if (receivers.length > 0) {
          const receiver = receivers[0]
          if (receiver.enabled_contact_types.includes('email')) {
            this.isEnableMail = true
            this.form.email = receiver.email
          }
        }
      } catch (error) {
        throw error
      }
    },
  },
}
</script>
